import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    OnChanges,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {DynamicFormService} from '../../../dynamic-form/dynamic-form.service';
import {BaseFieldComponent} from '../../base/base-field/base-field.component';
import {EventDispatcherService} from '../../../events/event-dispatcher.service';
import {TextFieldConfig} from '../text-field.config';
import {
    TextInputAutocompleteController,
} from '../../../autocomplete/text-input-autocomplete.controller';
import {TextAutocompleteConfig} from '../../../autocomplete/text-autocomplete.types';
import {TextAutocompletePanelComponent} from '../../../autocomplete/text-autocomplete-panel/text-autocomplete-panel.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'mydocma-form-text-field',
    templateUrl: './text-field.component.html',
    styleUrls: ['./text-field.component.scss'],
    providers: [AsyncPipe]
})
export class TextFieldComponent extends BaseFieldComponent<TextFieldConfig> implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('textInput', {static: false}) textInput: ElementRef<HTMLInputElement>;
    @ViewChild(TextAutocompletePanelComponent, {static: false}) autocompletePanel: TextAutocompletePanelComponent;

    public readonly staticCssClass = 'mydocma-text-field';

    public autocomplete: TextInputAutocompleteController;

    constructor(
        public readonly dynamicFormService: DynamicFormService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        public readonly eventDispatcher: EventDispatcherService,
        element: ElementRef,
        renderer: Renderer2,
        private readonly translateService: TranslateService
    ) {
        super(dynamicFormService, changeDetectorRef, eventDispatcher, element, renderer);
    }

    ngOnInit(): void {
        super.ngOnInit();

        const config: TextAutocompleteConfig = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) return;

        this.autocomplete = new TextInputAutocompleteController(
            this.control,
            () => this.textInput?.nativeElement ?? null,
            () => this.autocompletePanel ?? null,
            '.mydocma-text-field',
            this.changeDetectorRef,
            this.translateService
        );
        this.autocomplete.setup(config);
    }

    ngAfterViewInit(): void {}

    ngOnDestroy(): void {
        this.autocomplete?.destroy();
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        const config: TextAutocompleteConfig = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) return;

        this.autocomplete.setup(config);
    }

    public onClick(event?: MouseEvent) {
        super.onControlClick();

        const config: TextAutocompleteConfig = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || !event || this.fieldWrapper?.config?.readonly) return;
        
        const target = event.target as HTMLInputElement;
        if (!target || typeof target.selectionStart !== 'number') return;
        this.autocomplete.onFieldClick(target.value, target.selectionStart);
    }

    public onTextInput(event: Event) {
        const config: TextAutocompleteConfig = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) return;

        const target = event.target as HTMLInputElement;
        const value = target.value || '';
        const caretIndex = target.selectionStart != null ? target.selectionStart : value.length;
        this.autocomplete.onInput(value, caretIndex);
    }

    public onTextKeydown(event: KeyboardEvent) {
        const config: TextAutocompleteConfig = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) return;

        this.autocomplete.onKeydown(event);
    }

    public onBlur(event: FocusEvent) {
        const config: TextAutocompleteConfig = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) {
            this.eventDispatcher.notifyControlBlur(this.fieldWrapper.config);
            return;
        };

        const next = event.relatedTarget as HTMLElement | null;
        const panelEl = this.autocompletePanel?.panelEl?.nativeElement ?? null;

        // If focus moves into the autocomplete panel, do not treat it as a real blur.
        if (next && panelEl && panelEl.contains(next)) {
            return;
        }

        this.eventDispatcher.notifyControlBlur(this.fieldWrapper.config);
        this.autocomplete.closePanel();
    }

    public onAutocompleteOptionSelected(item: { value: string; display?: string }) {
        this.autocomplete.onOptionSelected(item);
    }

    public onAutocompleteOpened() {
        this.autocomplete.onPanelOpened();
    }

    public onAutocompleteClosed() {
        this.autocomplete.onPanelClosed();
    }
}
